import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Eye,
  ArrowLeftRight,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDate as fmtDateTz } from '../lib/datetime';
import { emptyComprobante, comprobanteTotals } from '../components/PurchaseInvoiceForm';
import { MovementDetailDialog } from '../components/treasury/MovementDetailDialog';
import { TreasuryDocDetailDialog } from '../components/treasury/TreasuryDocDetailDialog';
import { AdjustmentDialog, TransferDialog, CheckDialog, DepositCheckDialog, RejectCheckDialog, DeletePaymentOrderDialog, DeleteCollectionDialog, DeleteTreasuryDocDialog } from '../components/treasury/TreasuryDialogs';
import { SettleAdvanceDialog } from '../components/treasury/SettleAdvanceDialog';
import { CollectionDialog, PaymentOrderDialog } from '../components/treasury/PaymentDialogs';
import { PaymentsTabContent, ChecksTabContent, CollectionsTabContent, LedgerTabContent } from '../components/treasury/TreasuryTabs';
import { useTreasurySubmit, idempotencyHeader } from '../lib/useTreasurySubmit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] ?? false;
};

function TreasuryOperations() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  
  // Determinar el primer tab visible
  const getFirstVisibleTab = () => {
    if (userCan(user, 'tesoreria_cobranzas', 'ver')) return 'collections';
    if (userCan(user, 'tesoreria_pagos', 'ver')) return 'payments';
    if (userCan(user, 'tesoreria_ajustes', 'ver')) return 'adjustments';
    if (userCan(user, 'tesoreria_transferencias', 'ver')) return 'transfers';
    if (userCan(user, 'tesoreria_valores', 'ver')) return 'checks';
    if (userCan(user, 'tesoreria_libro', 'ver')) return 'ledger';
    return 'collections';
  };
  const [activeTab, setActiveTab] = useState(getFirstVisibleTab());
  const [loading, setLoading] = useState(true);
  // v3.28.1 · Guardas anti doble-submit para cada acción de tesorería
  const collectionSubmit = useTreasurySubmit();
  const paymentOrderSubmit = useTreasurySubmit();
  const adjustmentSubmit = useTreasurySubmit();
  const transferSubmit = useTreasurySubmit();
  
  // Data
  const [collections, setCollections] = useState([]);
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [showDeletedOps, setShowDeletedOps] = useState(false);
  const [deleteOpTarget, setDeleteOpTarget] = useState(null);
  const [deleteOpReason, setDeleteOpReason] = useState('');
  const [deletingOp, setDeletingOp] = useState(false);
  const canDeleteOp = user?.role === 'admin' || user?.permissions?.tesoreria_pagos?.eliminar;
  const handleDeleteOp = async () => {
    if (!deleteOpTarget) return;
    if ((deleteOpReason || '').trim().length < 10) {
      toast.error('El motivo es obligatorio (mín. 10 caracteres)');
      return;
    }
    setDeletingOp(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/treasury/payment-orders/${deleteOpTarget.payment_order_id}`,
        { withCredentials: true, data: { reason: deleteOpReason.trim() } }
      );
      toast.success(`OP ${deleteOpTarget.payment_order_number} eliminada`);
      setDeleteOpTarget(null);
      setDeleteOpReason('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la OP');
    } finally { setDeletingOp(false); }
  };
  const [deleteCollTarget, setDeleteCollTarget] = useState(null);
  const [deleteCollReason, setDeleteCollReason] = useState('');
  const [deletingColl, setDeletingColl] = useState(false);
  const canDeleteCollection = user?.role === 'admin' || user?.permissions?.tesoreria_cobranzas?.eliminar;
  const handleDeleteCollection = async () => {
    if (!deleteCollTarget) return;
    if ((deleteCollReason || '').trim().length < 10) {
      toast.error('El motivo es obligatorio (mín. 10 caracteres)');
      return;
    }
    setDeletingColl(true);
    try {
      let r;
      if (deleteCollTarget.receipt_id) {
        // Cobranza espejo de un recibo de convenio → se anula el recibo (reversa total)
        r = await axios.post(
          `${BACKEND_URL}/api/insurer-receipts/${deleteCollTarget.receipt_id}/void`,
          { reason: deleteCollReason.trim() }, { withCredentials: true }
        );
        const revs = r.data?.reversal_entries || [];
        toast.success(`Recibo ${deleteCollTarget.receipt_number} anulado`
          + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : ''));
      } else {
        r = await axios.delete(
          `${BACKEND_URL}/api/treasury/collections/${deleteCollTarget.collection_id}`,
          { withCredentials: true, data: { reason: deleteCollReason.trim() } }
        );
        const revs = r.data?.reversal_entries || [];
        toast.success(`Cobranza ${deleteCollTarget.collection_number} eliminada`
          + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : ''));
      }
      setDeleteCollTarget(null);
      setDeleteCollReason('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la cobranza');
    } finally { setDeletingColl(false); }
  };
  const [unsettleTarget, setUnsettleTarget] = useState(null);
  const [unsettleReason, setUnsettleReason] = useState('');
  const [unsettling, setUnsettling] = useState(false);
  const canUnsettleAdvance = user?.role === 'admin' || user?.permissions?.tesoreria_pagos?.anular_rendicion;
  const handleUnsettle = async () => {
    if ((unsettleReason || '').trim().length < 10) {
      toast.error('El motivo es obligatorio (mín. 10 caracteres)');
      return;
    }
    setUnsettling(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/treasury/payment-orders/${unsettleTarget.payment_order_id}/unsettle`,
        { reason: unsettleReason.trim() }, { withCredentials: true }
      );
      const revs = r.data?.reversal_entries || [];
      const comps = r.data?.deleted_comprobantes || [];
      toast.success(`Rendición anulada · Anticipo ${unsettleTarget.payment_order_number} reabierto`
        + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : '')
        + (comps.length ? ` · Comprobantes dados de baja: ${comps.join(', ')}` : ''));
      setUnsettleTarget(null); setUnsettleReason('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo anular la rendición');
    } finally { setUnsettling(false); }
  };

  const [deleteAdjTarget, setDeleteAdjTarget] = useState(null);
  const [deleteAdjReason, setDeleteAdjReason] = useState('');
  const [deletingAdj, setDeletingAdj] = useState(false);
  const canDeleteAdjustment = user?.role === 'admin' || user?.permissions?.tesoreria_ajustes?.eliminar;
  const [deleteTrTarget, setDeleteTrTarget] = useState(null);
  const [deleteTrReason, setDeleteTrReason] = useState('');
  const [deletingTr, setDeletingTr] = useState(false);
  const canDeleteTransfer = user?.role === 'admin' || user?.permissions?.tesoreria_transferencias?.eliminar;

  // v3.47.19 · Revertir ajuste puro de cta.cte. (AJCC) desde la pestaña Ajustes
  const [revertCCTarget, setRevertCCTarget] = useState(null);
  const [revertCCReason, setRevertCCReason] = useState('');
  const [revertingCC, setRevertingCC] = useState(false);

  const revertWithReason = async (path, reason, label, setBusy, onDone) => {
    if ((reason || '').trim().length < 10) {
      toast.error('El motivo es obligatorio (mín. 10 caracteres)');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}${path}`, { reason: reason.trim() }, { withCredentials: true });
      const revs = r.data?.reversal_entries || [];
      toast.success(`${label} revertido` + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : ''));
      onDone();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `No se pudo revertir ${label}`);
    } finally { setBusy(false); }
  };

  const deleteWithReason = async (path, reason, label, setBusy, onDone) => {
    if ((reason || '').trim().length < 10) {
      toast.error('El motivo es obligatorio (mín. 10 caracteres)');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.delete(`${BACKEND_URL}${path}`, { withCredentials: true, data: { reason: reason.trim() } });
      const revs = r.data?.reversal_entries || [];
      toast.success(`${label} eliminado` + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : ''));
      onDone();
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `No se pudo eliminar ${label}`);
    } finally { setBusy(false); }
  };

  const [adjustments, setAdjustments] = useState([]);
  const [ccAdjustments, setCCAdjustments] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [checks, setChecks] = useState([]);
  const [movements, setMovements] = useState([]);
  
  // Maestros
  const [cashBanks, setCashBanks] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [withholdingTypes, setWithholdingTypes] = useState([]);
  const [residents, setResidents] = useState([]);
  const [residentsAvailable, setResidentsAvailable] = useState(true);
  
  // Dialogs
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [paymentOrderDialogOpen, setPaymentOrderDialogOpen] = useState(false);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleAdvance, setSettleAdvance] = useState(null);
  const [settling, setSettling] = useState(false);
  const [settleForm, setSettleForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    expenses: [{ expense_concept_id: '', amount: '', reference: '' }],
    difference_cash_bank_id: '',
    comprobantes: [],
  });
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [viewMovementOpen, setViewMovementOpen] = useState(false);
  const [docDetail, setDocDetail] = useState(null);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingCheck, setRejectingCheck] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Filtros por solapa
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    cashBankId: '',
    search: ''
  });
  
  // Forms
  const [collectionForm, setCollectionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    payer_type: 'other', // 'resident' | 'other'
    resident_id: '',
    cash_bank_id: '',
    income_concept_id: '',
    description: '',
    client_name: '',
    payment_details: [{ payment_type_id: '', amount: '', reference: '', due_date: '' }]
  });
  
  const [paymentOrderForm, setPaymentOrderForm] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_bank_id: '',
    expense_concept_id: '',
    description: '',
    recipient_type: 'other',
    supplier_id: '',
    recipient_name: '',
    payroll_data: null,
    payment_details: [{ payment_type_id: '', amount: '', reference: '', locked: false }],
    check_id: null,
    is_advance: false,
    cancel_supplier_debt: false
  });
  const [supplierPending, setSupplierPending] = useState([]);
  // v3.38.3 — Saldo a favor del proveedor (anticipos de OPs sin consumir + NCs sin netear)
  const [supplierAdvanceTotal, setSupplierAdvanceTotal] = useState(0);
  const [supplierNcCreditTotal, setSupplierNcCreditTotal] = useState(0);
  const [residentPending, setResidentPending] = useState({ invoices: [], available_advance: 0, total_outstanding: 0 });
  
  const [adjustmentForm, setAdjustmentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    adjustment_type: 'debit',
    cash_bank_id: '',
    concept_type: 'income',
    concept_id: '',
    amount: '',
    description: '',
    target_type: 'none',   // 'none' | 'supplier' | 'resident'
    target_id: '',
    target_label: '',      // display-only para el diálogo
  });
  
  const [transferForm, setTransferForm] = useState({
    date: new Date().toISOString().split('T')[0],
    origin_cash_bank_id: '',
    destination_cash_bank_id: '',
    amount: '',
    description: ''
  });
  
  const [checkForm, setCheckForm] = useState({
    check_number: '',
    issuer: '',
    bank: '',
    amount: '',
    currency: 'ARS',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: ''
  });
  
  const canCreate = userCan(user, 'tesoreria', 'crear');
  
  // Permisos granulares por funcion
  const canViewCobranzas = userCan(user, 'tesoreria_cobranzas', 'ver');
  const canViewPagos = userCan(user, 'tesoreria_pagos', 'ver');
  const canViewAjustes = userCan(user, 'tesoreria_ajustes', 'ver');
  const canViewTransferencias = userCan(user, 'tesoreria_transferencias', 'ver');
  const canViewValores = userCan(user, 'tesoreria_valores', 'ver');
  const canViewLibro = userCan(user, 'tesoreria_libro', 'ver');
  const canCreateCobranzas = userCan(user, 'tesoreria_cobranzas', 'crear');
  const canCreatePagos = userCan(user, 'tesoreria_pagos', 'crear');
  const canCreateAjustes = userCan(user, 'tesoreria_ajustes', 'crear');
  const canCreateTransferencias = userCan(user, 'tesoreria_transferencias', 'crear');
  
  // Funciones de filtrado y ordenamiento
  const applyFilters = (items, dateField = 'date', searchFields = []) => {
    let filtered = [...items];
    
    if (filters.dateFrom) {
      filtered = filtered.filter(item => {
        const d = item[dateField];
        return d && new Date(d) >= new Date(filters.dateFrom);
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(item => {
        const d = item[dateField];
        return d && new Date(d) <= new Date(filters.dateTo + 'T23:59:59');
      });
    }
    if (filters.cashBankId) {
      filtered = filtered.filter(item => 
        item.cash_bank_id === filters.cashBankId || 
        item.origin_cash_bank_id === filters.cashBankId ||
        item.destination_cash_bank_id === filters.cashBankId
      );
    }
    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      // v3.36.16 · También buscar por importe (tolerante a $, puntos y comas de miles/decimales)
      const normalized = q.replace(/[$\s]/g, '').replace(/\./g, '').replace(',', '.');
      const qNum = parseFloat(normalized);
      const hasAmount = !Number.isNaN(qNum) && isFinite(qNum) && normalized !== '';
      filtered = filtered.filter(item => {
        const textMatch = searchFields.some(f => String(item[f] || '').toLowerCase().includes(q));
        if (textMatch) return true;
        if (!hasAmount) return false;
        // Campos de importe según el tipo de operación
        const amountFields = ['total_amount', 'amount', 'cash_paid_total', 'deferred_check_total'];
        return amountFields.some(af => {
          const v = item[af];
          if (v === undefined || v === null) return false;
          const n = typeof v === 'number' ? v : parseFloat(v);
          if (Number.isNaN(n)) return false;
          // Match exacto con tolerancia de redondeo
          return Math.abs(n - qNum) < 0.01;
        });
      });
    }
    
    // v3.28.31 · Orden por número correlativo DESC (respeta secuencia de emisión).
    // Fallback a fecha si no hay número correlativo (ej. movimientos sin numerar).
    // Antes se ordenaba por date DESC → tras la renumeración de Tesorería la 1ª y 2ª
    // secuencia se mezclaban por tener misma fecha (bug reportado por Diego 11/02/2026).
    const numberField = searchFields[0];
    filtered.sort((a, b) => {
      const numA = a[numberField];
      const numB = b[numberField];
      if (numA && numB) {
        if (numA !== numB) return String(numB).localeCompare(String(numA));
      }
      const dateA = new Date(a[dateField] || 0);
      const dateB = new Date(b[dateField] || 0);
      if (dateB.getTime() !== dateA.getTime()) return dateB - dateA;
      // Misma fecha: ordenar por created_at
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    
    return filtered;
  };
  
  const filteredCollections = applyFilters(collections, 'date', ['collection_number', 'client_name', 'income_concept_name', 'description']);
  const filteredPaymentOrders = applyFilters(paymentOrders, 'date', ['payment_order_number', 'recipient_name', 'supplier_name', 'expense_concept_name', 'description'])
    .filter(po => showDeletedOps || !po.deleted_at);
  const filteredAdjustments = applyFilters(adjustments, 'date', ['adjustment_number', 'description', 'concept_name', 'cash_bank_name']);
  const filteredCCAdjustments = applyFilters(ccAdjustments, 'date', ['adjustment_number', 'description', 'concept_name']);
  const filteredTransfers = applyFilters(transfers, 'date', ['transfer_number', 'description', 'origin_cash_bank_name', 'destination_cash_bank_name']);
  const filteredChecks = applyFilters(checks, 'due_date', ['check_number', 'issuer', 'notes']);
  const filteredMovements = applyFilters(movements, 'date', ['reference_number', 'concept_name', 'description', 'cash_bank_name']);
  
  const clearFilters = () => setFilters({ dateFrom: '', dateTo: '', cashBankId: '', search: '' });
  
  const isCheckPaymentType = (ptId) => {
    const pt = paymentTypes.find(p => p.payment_type_id === ptId);
    if (!pt) return false;
    const name = pt.name.toLowerCase();
    return name.includes('cheque') || name.includes('echeq') || name.includes('e.cheq') || name.includes('e-cheq');
  };

  const isDeferredCheckPaymentType = (ptId) => {
    const pt = paymentTypes.find(p => p.payment_type_id === ptId);
    if (!pt) return false;
    const n = (pt.name || '').toLowerCase().replace(/[.\-\s]/g, '');
    return n.includes('diferid') || n.includes('echeq');
  };

  const isCashPaymentType = (ptId) => {
    const pt = paymentTypes.find(p => p.payment_type_id === ptId);
    if (!pt) return false;
    return pt.name.toLowerCase().trim() === 'efectivo';
  };

  // v3.28.10 · Fecha valor
  const isTransferPaymentType = (ptId) => {
    const pt = paymentTypes.find(p => p.payment_type_id === ptId);
    if (!pt) return false;
    return pt.name.toLowerCase().includes('transferencia');
  };

  const getCashBank = (cbId) => cashBanks.find(cb => cb.cash_bank_id === cbId);
  const isBankAccount = (cbId) => getCashBank(cbId)?.entity_type === 'bank';

  // Detecta el conflicto: cuenta bancaria + medios de pago en efectivo
  const detectCashIntoBankConflict = (cashBankId, paymentDetails) => {
    if (!isBankAccount(cashBankId)) return null;
    const cashRows = paymentDetails
      .map((pd, idx) => ({ idx, pd }))
      .filter(({ pd }) => pd.payment_type_id && isCashPaymentType(pd.payment_type_id));
    if (cashRows.length === 0) return null;
    const cbName = getCashBank(cashBankId)?.name || 'la cuenta';
    return {
      indexes: cashRows.map(r => r.idx),
      message: `No se puede usar "Efectivo" con cuenta bancaria (${cbName}). Elegí transferencia, cheque o depósito.`,
    };
  };
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    try {
      const safe = (promise) => promise.catch(() => ({ data: [] }));
      const [
        collectionsRes, paymentOrdersRes, adjustmentsRes, ccAdjustmentsRes, transfersRes, checksRes, movementsRes,
        cashBanksRes, incomeConceptsRes, expenseConceptsRes, paymentTypesRes, suppliersRes, withholdingTypesRes, residentsRes
      ] = await Promise.all([
        safe(axios.get(`${BACKEND_URL}/api/treasury/collections`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/payment-orders?include_deleted=true`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/adjustments`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/cc-adjustments/all`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/transfers`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/checks`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/movements?limit=50`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/cash-banks/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/income-concepts/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/expense-concepts/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/payment-types/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/suppliers/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/withholding-types/active`, { withCredentials: true })),
        axios.get(`${BACKEND_URL}/api/residents?status=activo`, { withCredentials: true }).catch(err => ({ data: [], _forbidden: err?.response?.status === 403 })),
      ]);
      
      setCollections(collectionsRes.data);
      setPaymentOrders(paymentOrdersRes.data);
      setAdjustments(adjustmentsRes.data);
      setCCAdjustments(Array.isArray(ccAdjustmentsRes.data) ? ccAdjustmentsRes.data : []);
      setTransfers(transfersRes.data);
      setChecks(checksRes.data);
      setMovements(movementsRes.data);
      setCashBanks(cashBanksRes.data);
      setIncomeConcepts(incomeConceptsRes.data);
      setExpenseConcepts(expenseConceptsRes.data);
      setPaymentTypes(paymentTypesRes.data);
      setSuppliers(suppliersRes.data);
      setWithholdingTypes(withholdingTypesRes.data);
      setResidents(Array.isArray(residentsRes.data) ? residentsRes.data : []);
      setResidentsAvailable(!residentsRes._forbidden);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  // ============ COBRANZAS ============
  const addPaymentDetail = () => {
    setCollectionForm({
      ...collectionForm,
      payment_details: [...collectionForm.payment_details, { payment_type_id: '', amount: '', reference: '', due_date: '' }]
    });
  };
  
  const removePaymentDetail = (index) => {
    if (collectionForm.payment_details.length > 1) {
      setCollectionForm({
        ...collectionForm,
        payment_details: collectionForm.payment_details.filter((_, i) => i !== index)
      });
    }
  };
  
  const updatePaymentDetail = (index, field, value) => {
    const newDetails = [...collectionForm.payment_details];
    newDetails[index][field] = value;
    setCollectionForm({ ...collectionForm, payment_details: newDetails });
  };
  
  const handleSaveCollection = async () => {
    return collectionSubmit.run(async (idemKey) => {
    const isResident = collectionForm.payer_type === 'resident';
    if (!collectionForm.cash_bank_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    if (isResident && !collectionForm.resident_id) {
      toast.error('Seleccioná el residente');
      return;
    }
    if (!isResident && !collectionForm.income_concept_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    const validDetails = collectionForm.payment_details.filter(pd => pd.payment_type_id && pd.amount > 0);
    if (validDetails.length === 0) {
      toast.error('Agrega al menos un medio de pago');
      return;
    }

    const conflict = detectCashIntoBankConflict(collectionForm.cash_bank_id, validDetails);
    if (conflict) {
      toast.error(conflict.message);
      return;
    }
    
    try {
      const details = validDetails.map(pd => ({
        payment_type_id: pd.payment_type_id,
        amount: parseFloat(pd.amount),
        reference: pd.reference || null,
        due_date: pd.due_date || null,
        value_date: pd.value_date || null,  // v3.28.10
        cash_bank_id: collectionForm.cash_bank_id,
      }));
      let resp;
      if (isResident) {
        const allocations = residentPending.invoices
          .filter(i => i.selected && parseFloat(i.payAmount) > 0)
          .map(i => ({ invoice_id: i.invoice_id, amount: parseFloat(i.payAmount) }));
        resp = await axios.post(`${BACKEND_URL}/api/residents/${collectionForm.resident_id}/collect`, {
          date: collectionForm.date,
          description: collectionForm.description || null,
          payment_details: details,
          allocations: allocations.length ? allocations : null,
          auto_apply: true,  // el excedente sigue imputándose FIFO y luego queda a favor
        }, { withCredentials: true, headers: idempotencyHeader(idemKey) });
      } else {
        resp = await axios.post(`${BACKEND_URL}/api/treasury/collections`, {
          date: collectionForm.date,
          cash_bank_id: collectionForm.cash_bank_id,
          income_concept_id: collectionForm.income_concept_id,
          description: collectionForm.description,
          client_name: collectionForm.client_name,
          payment_details: details,
        }, { withCredentials: true, headers: idempotencyHeader(idemKey) });
      }
      
      const collectionId = resp.data.collection_id;
      const number = resp.data.collection_number || 'registrada';
      const advance = resp.data.advance || 0;
      toast.success(`Cobranza ${number}${advance > 0 ? ` · Anticipo $${advance.toLocaleString('es-AR')}` : ''}`);
      if (collectionId) {
        window.open(`${BACKEND_URL}/api/treasury/collections/${collectionId}/pdf`, '_blank');
      }
      setCollectionDialogOpen(false);
      resetCollectionForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    });
  };
  
  const resetCollectionForm = () => {
    setCollectionForm({
      date: new Date().toISOString().split('T')[0],
      payer_type: 'other',
      resident_id: '',
      cash_bank_id: '',
      income_concept_id: '',
      description: '',
      client_name: '',
      payment_details: [{ payment_type_id: '', amount: '', reference: '', due_date: '' }]
    });
    setResidentPending({ invoices: [], available_advance: 0, total_outstanding: 0 });
  };
  
  // ============ ÓRDENES DE PAGO ============
  const handleSavePaymentOrder = async () => {
    return paymentOrderSubmit.run(async (idemKey) => {
    const validDetails = paymentOrderForm.payment_details.filter(pd => pd.payment_type_id && pd.amount);
    const cancelDebt = paymentOrderForm.cancel_supplier_debt;
    if (!paymentOrderForm.cash_bank_id || validDetails.length === 0 ||
        (!paymentOrderForm.is_advance && !cancelDebt && !paymentOrderForm.expense_concept_id)) {
      toast.error('Completa los campos obligatorios y al menos un medio de pago');
      return;
    }
    
    if (paymentOrderForm.recipient_type === 'supplier' && !paymentOrderForm.supplier_id) {
      toast.error('Selecciona un proveedor');
      return;
    }
    
    if (paymentOrderForm.recipient_type === 'other' && !paymentOrderForm.recipient_name) {
      toast.error('Ingresa el destinatario');
      return;
    }
    if (paymentOrderForm.recipient_type === 'payroll') {
      const pd = paymentOrderForm.payroll_data;
      if (!pd || !pd.payroll_beneficiaries?.length) {
        toast.error('Adjuntá el archivo .txt del homebanking con la nómina');
        return;
      }
      if (pd.has_variance && !pd.variance_ok) {
        toast.error('El archivo difiere del total de la OP: aclará el motivo del desvío');
        return;
      }
      if (!paymentOrderForm.recipient_name) {
        toast.error('Ingresá una descripción del lote (ej. "Sueldos Setiembre 2026")');
        return;
      }
    }

    let debtAllocations = null;
    let payAdvance = 0;  // v3.37.2
    if (cancelDebt) {
      const selected = supplierPending.filter(p => p.selected && parseFloat(p.payAmount) > 0);
      for (const p of selected) {
        if (parseFloat(p.payAmount) > p.outstanding + 0.01) { toast.error(`El importe a cancelar de ${p.label} supera su saldo`); return; }
      }
      debtAllocations = selected.map(p => ({ purchase_invoice_id: p.purchase_invoice_id, amount: parseFloat(p.payAmount) }));
    }

    const conflict = detectCashIntoBankConflict(paymentOrderForm.cash_bank_id, validDetails);
    if (conflict) {
      toast.error(conflict.message);
      return;
    }
    
    const totalAmount = validDetails.reduce((s, pd) => s + parseFloat(pd.amount), 0);
    if (cancelDebt) {
      const allocTotal = (debtAllocations || []).reduce((s, a) => s + a.amount, 0);
      if (allocTotal > totalAmount + 0.01) {
        toast.error(`Lo asignado a comprobantes (${formatCurrency(allocTotal)}) supera el total del pago (${formatCurrency(totalAmount)})`);
        return;
      }
      payAdvance = Math.max(0, Math.round((totalAmount - allocTotal) * 100) / 100);  // v3.37.2
    }
    
    // Validar fecha de pago para cheques diferidos / e.Cheq
    for (const pd of validDetails) {
      if (isDeferredCheckPaymentType(pd.payment_type_id) && !pd.due_date) {
        toast.error('Indicá la fecha de pago del cheque diferido / e.Cheq');
        return;
      }
    }

    try {
      const orderData = {
        date: paymentOrderForm.date,
        cash_bank_id: paymentOrderForm.cash_bank_id,
        expense_concept_id: cancelDebt ? null : (paymentOrderForm.expense_concept_id || null),
        description: paymentOrderForm.description,
        recipient_type: paymentOrderForm.recipient_type,
        supplier_id: paymentOrderForm.supplier_id,
        recipient_name: paymentOrderForm.recipient_name,
        is_advance: paymentOrderForm.is_advance,
        cancel_supplier_debt: cancelDebt,
        debt_allocations: debtAllocations,
        payment_details: validDetails.map(pd => ({
          payment_type_id: pd.payment_type_id,
          amount: parseFloat(pd.amount),
          reference: pd.reference || null,
          due_date: pd.due_date || null,
          value_date: pd.value_date || null,  // v3.28.10
          check_id: pd.check_id || null
        })),
        ...(paymentOrderForm.recipient_type === 'payroll' && paymentOrderForm.payroll_data ? {
          payroll_beneficiaries: paymentOrderForm.payroll_data.payroll_beneficiaries,
          payroll_attachment: paymentOrderForm.payroll_data.payroll_attachment,
          payroll_variance_reason: paymentOrderForm.payroll_data.payroll_variance_reason,
        } : {})
      };
      
      const opResp = await axios.post(`${BACKEND_URL}/api/treasury/payment-orders`, orderData, { withCredentials: true, headers: idempotencyHeader(idemKey) });

      const opId = opResp.data?.payment_order_id;
      const opNumber = opResp.data?.payment_order_number || 'registrada';
      toast.success(paymentOrderForm.is_advance ? `Anticipo a rendir ${opNumber} registrado` : (payAdvance > 0 ? `Orden de pago ${opNumber} registrada · saldo a favor ${formatCurrency(payAdvance)}` : `Orden de pago ${opNumber} registrada`));
      if (opId && !paymentOrderForm.is_advance) {
        // v3.30.11.2 · Auto-abre el PDF de la OP recién creada (paridad con Cobranzas)
        window.open(`${BACKEND_URL}/api/treasury/payment-orders/${opId}/pdf?t=${Date.now()}`, '_blank');
      }
      setPaymentOrderDialogOpen(false);
      resetPaymentOrderForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    });
  };
  
  const resetPaymentOrderForm = () => {
    setSupplierPending([]);
    setSupplierAdvanceTotal(0);
    setSupplierNcCreditTotal(0);
    setPaymentOrderForm({
      date: new Date().toISOString().split('T')[0],
      cash_bank_id: '',
      expense_concept_id: '',
      description: '',
      recipient_type: 'other',
      supplier_id: '',
      recipient_name: '',
      payment_details: [{ payment_type_id: '', amount: '', reference: '', locked: false }],
      check_id: null,
      is_advance: false,
      cancel_supplier_debt: false
    });
  };

  const openSettleDialog = (advance) => {
    setSettleAdvance(advance);
    setSettleForm({
      date: new Date().toISOString().slice(0, 10),
      expenses: [{ expense_concept_id: advance.expense_concept_id || '', amount: '', reference: '' }],
      difference_cash_bank_id: advance.cash_bank_id || '',
      comprobantes: [],
    });
    setSettleDialogOpen(true);
  };

  const settleExpensesTotal = settleForm.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const settleComprobantesTotal = (settleForm.comprobantes || []).reduce((s, c) => s + comprobanteTotals(c).total, 0);
  const settleRendido = Math.round((settleExpensesTotal + settleComprobantesTotal) * 100) / 100;
  const settleDiff = settleAdvance ? Math.round((settleAdvance.total_amount - settleRendido) * 100) / 100 : 0;

  const addSettleExpense = () => setSettleForm({ ...settleForm, expenses: [...settleForm.expenses, { expense_concept_id: '', amount: '', reference: '' }] });
  const rmSettleExpense = (i) => setSettleForm({ ...settleForm, expenses: settleForm.expenses.filter((_, j) => j !== i) });
  const setSettleExpense = (i, patch) => setSettleForm({ ...settleForm, expenses: settleForm.expenses.map((e, j) => j === i ? { ...e, ...patch } : e) });

  const addComprobante = () => setSettleForm(f => ({ ...f, comprobantes: [...f.comprobantes, emptyComprobante()] }));
  const setComprobante = (i, val) => setSettleForm(f => ({ ...f, comprobantes: f.comprobantes.map((c, j) => j === i ? val : c) }));
  const rmComprobante = (i) => setSettleForm(f => ({ ...f, comprobantes: f.comprobantes.filter((_, j) => j !== i) }));

  const mapComprobantesPayload = (comps) => comps.map(c => ({
    supplier_id: c.supplier_id || null,
    supplier_business_name: c.supplier_business_name || null,
    supplier_tax_id: c.supplier_tax_id || null,
    supplier_tax_condition: c.supplier_tax_condition || null,
    expense_concept_id: c.expense_concept_id || null,
    fecha_comprobante: c.fecha_comprobante,
    tipo_comprobante: c.tipo_comprobante,
    punto_venta: c.punto_venta || null,
    numero: c.numero || null,
    items_iva: (c.items_iva || []).filter(it => parseFloat(it.base_neta) > 0).map(it => ({ base_neta: parseFloat(it.base_neta), alicuota: parseFloat(it.alicuota) || 0 })),
    no_gravado: parseFloat(c.no_gravado) || 0,
    exento: parseFloat(c.exento) || 0,
    percepciones: (c.percepciones || []).filter(p => parseFloat(p.monto) > 0).map(p => ({ tipo: p.tipo, withholding_type_id: p.withholding_type_id || null, base: parseFloat(p.base) || 0, alicuota: p.alicuota ? parseFloat(p.alicuota) : null, monto: parseFloat(p.monto) })),
    attachment_id: c.attachment_id || null,
  }));

  const handleSettle = async () => {
    const valid = settleForm.expenses.filter(e => e.expense_concept_id && parseFloat(e.amount) > 0);
    const comps = settleForm.comprobantes || [];
    // Validar comprobantes: requieren concepto imputado e importe > 0
    for (let i = 0; i < comps.length; i++) {
      if (comprobanteTotals(comps[i]).total <= 0) { toast.error(`Comprobante #${i + 1}: cargá un importe (neto/IVA) válido`); return; }
      if (!comps[i].expense_concept_id) { toast.error(`Comprobante #${i + 1}: seleccioná el Concepto de Gasto (imputación)`); return; }
    }
    if (valid.length === 0 && comps.length === 0) { toast.error('Cargá al menos un gasto o un comprobante fiscal'); return; }
    if (settleDiff !== 0 && !settleForm.difference_cash_bank_id) {
      toast.error('Indicá la caja para la devolución/reposición de la diferencia');
      return;
    }
    setSettling(true);
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/payment-orders/${settleAdvance.payment_order_id}/settle`, {
        date: settleForm.date,
        expenses: valid.map(e => ({ expense_concept_id: e.expense_concept_id, amount: parseFloat(e.amount), reference: e.reference })),
        difference_cash_bank_id: settleDiff !== 0 ? settleForm.difference_cash_bank_id : null,
        comprobantes: comps.length ? mapComprobantesPayload(comps) : null,
      }, { withCredentials: true });
      toast.success('Anticipo rendido y cerrado');
      setSettleDialogOpen(false);
      setSettleAdvance(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rendir el anticipo');
    } finally {
      setSettling(false);
    }
  };

  
  const addPOPaymentDetail = () => {
    setPaymentOrderForm({
      ...paymentOrderForm,
      payment_details: [...paymentOrderForm.payment_details, { payment_type_id: '', amount: '', reference: '', locked: false }]
    });
  };
  
  const updatePOPaymentDetail = (index, field, value) => {
    const details = [...paymentOrderForm.payment_details];
    details[index] = { ...details[index], [field]: value };
    setPaymentOrderForm({ ...paymentOrderForm, payment_details: details });
  };
  
  const removePOPaymentDetail = (index) => {
    if (paymentOrderForm.payment_details.length <= 1) return;
    const details = paymentOrderForm.payment_details.filter((_, i) => i !== index);
    setPaymentOrderForm({ ...paymentOrderForm, payment_details: details });
  };

  // ===== FASE 2: cancelación de deuda de cuenta corriente de proveedores =====
  const loadSupplierPending = async (supplierId) => {
    if (!supplierId) {
      setSupplierPending([]);
      setSupplierAdvanceTotal(0);
      setSupplierNcCreditTotal(0);
      return;
    }
    try {
      // v3.38.3 — Pedimos el objeto enriquecido (?include_advance=true) para mostrar
      // el saldo a favor del proveedor y permitir aplicarlo con un clic.
      const r = await axios.get(
        `${BACKEND_URL}/api/supplier-accounts/${supplierId}/pending?include_advance=true`,
        { withCredentials: true }
      );
      const body = r.data || {};
      setSupplierPending((body.pending || []).map(p => ({ ...p, selected: false, payAmount: p.outstanding })));
      setSupplierAdvanceTotal(Number(body.available_advance_total || 0));
      setSupplierNcCreditTotal(Number(body.available_nc_credit_total || 0));
    } catch {
      setSupplierPending([]);
      setSupplierAdvanceTotal(0);
      setSupplierNcCreditTotal(0);
    }
  };
  const applySupplierAdvanceAuto = async (supplierId) => {
    // v3.38.3 — Aplica FIFO el saldo a favor del proveedor a las FCs pendientes.
    if (!supplierId || !supplierAdvanceTotal || supplierAdvanceTotal <= 0.009) return;
    try {
      const sorted = [...supplierPending].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      let remaining = supplierAdvanceTotal;
      const allocations = [];
      for (const p of sorted) {
        if (remaining <= 0.009) break;
        const amt = Math.min(remaining, Number(p.outstanding || 0));
        if (amt > 0.009) {
          allocations.push({ purchase_invoice_id: p.purchase_invoice_id, amount: Math.round(amt * 100) / 100 });
          remaining -= amt;
        }
      }
      if (!allocations.length) {
        toast.info('No hay facturas pendientes para netear el saldo a favor');
        return;
      }
      await axios.post(
        `${BACKEND_URL}/api/supplier-accounts/${supplierId}/apply-advance`,
        { allocations, date: new Date().toISOString().slice(0, 10) },
        { withCredentials: true }
      );
      const applied = allocations.reduce((s, a) => s + a.amount, 0);
      toast.success(`Se aplicaron ${(applied || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })} de saldo a favor`);
      await loadSupplierPending(supplierId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar el saldo a favor');
    }
  };
  const togglePendingSelect = (pid) => setSupplierPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, selected: !p.selected } : p));
  const updatePendingAmount = (pid, val) => setSupplierPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, payAmount: val } : p));
  const debtAllocationsTotal = supplierPending.filter(p => p.selected).reduce((s, p) => s + (parseFloat(p.payAmount) || 0), 0);

  // ===== v2.92.0: imputación de la cobranza a los comprobantes de la CC del residente =====
  const loadResidentPending = async (residentId) => {
    if (!residentId) { setResidentPending({ invoices: [], available_advance: 0, total_outstanding: 0 }); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/${residentId}/pending-invoices`, { withCredentials: true });
      setResidentPending({
        ...r.data,
        invoices: (r.data.invoices || []).map(i => ({ ...i, selected: false, payAmount: i.outstanding })),
      });
    } catch { setResidentPending({ invoices: [], available_advance: 0, total_outstanding: 0 }); }
  };
  const toggleResidentInvoice = (iid) => setResidentPending(prev => ({
    ...prev,
    invoices: prev.invoices.map(i => i.invoice_id === iid ? { ...i, selected: !i.selected } : i),
  }));
  const updateResidentInvoiceAmount = (iid, val) => setResidentPending(prev => ({
    ...prev,
    invoices: prev.invoices.map(i => i.invoice_id === iid ? { ...i, payAmount: val } : i),
  }));
  const residentAllocTotal = residentPending.invoices
    .filter(i => i.selected).reduce((sum, i) => sum + (parseFloat(i.payAmount) || 0), 0);

  
  // ============ AJUSTES ============
  const handleSaveAdjustment = async () => {
    return adjustmentSubmit.run(async (idemKey) => {
    if (!adjustmentForm.cash_bank_id || !adjustmentForm.concept_id || !adjustmentForm.amount) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    if (adjustmentForm.target_type !== 'none' && !adjustmentForm.target_id) {
      toast.error('Elegí el proveedor o residente destino');
      return;
    }

    try {
      const payload = {
        date: adjustmentForm.date,
        adjustment_type: adjustmentForm.adjustment_type,
        cash_bank_id: adjustmentForm.cash_bank_id,
        concept_type: adjustmentForm.concept_type,
        concept_id: adjustmentForm.concept_id,
        amount: parseFloat(adjustmentForm.amount),
        description: adjustmentForm.description,
        target_type: adjustmentForm.target_type,
        target_id: adjustmentForm.target_type === 'none' ? null : adjustmentForm.target_id,
      };
      await axios.post(`${BACKEND_URL}/api/treasury/adjustments`, payload, { withCredentials: true, headers: idempotencyHeader(idemKey) });

      toast.success('Ajuste registrado');
      setAdjustmentDialogOpen(false);
      resetAdjustmentForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    });
  };

  const resetAdjustmentForm = () => {
    setAdjustmentForm({
      date: new Date().toISOString().split('T')[0],
      adjustment_type: 'debit',
      cash_bank_id: '',
      concept_type: 'income',
      concept_id: '',
      amount: '',
      description: '',
      target_type: 'none',
      target_id: '',
      target_label: '',
    });
  };
  
  // ============ TRANSFERENCIAS ============
  const handleSaveTransfer = async () => {
    return transferSubmit.run(async (idemKey) => {
    if (!transferForm.origin_cash_bank_id || !transferForm.destination_cash_bank_id || !transferForm.amount) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/transfers`, {
        ...transferForm,
        amount: parseFloat(transferForm.amount)
      }, { withCredentials: true, headers: idempotencyHeader(idemKey) });
      
      toast.success('Transferencia registrada');
      setTransferDialogOpen(false);
      resetTransferForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    });
  };
  
  const resetTransferForm = () => {
    setTransferForm({
      date: new Date().toISOString().split('T')[0],
      origin_cash_bank_id: '',
      destination_cash_bank_id: '',
      amount: '',
      description: ''
    });
  };
  
  // ============ CHEQUES ============
  const handleSaveCheck = async () => {
    if (!checkForm.check_number || !checkForm.issuer || !checkForm.amount || !checkForm.due_date) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/checks`, {
        ...checkForm,
        amount: parseFloat(checkForm.amount)
      }, { withCredentials: true });
      
      toast.success('Cheque registrado');
      setCheckDialogOpen(false);
      resetCheckForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };
  
  const resetCheckForm = () => {
    setCheckForm({
      check_number: '',
      issuer: '',
      bank: '',
      amount: '',
      currency: 'ARS',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: ''
    });
  };
  
  const handleDepositCheck = async (cashBankId) => {
    if (!selectedCheck) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/treasury/checks/${selectedCheck.check_id}/deposit?cash_bank_id=${cashBankId}`,
        {},
        { withCredentials: true }
      );
      
      toast.success('Cheque depositado');
      setDepositDialogOpen(false);
      setSelectedCheck(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al depositar');
    }
  };
  
  const handleReturnCheck = (check) => {
    setRejectingCheck(check);
    setRejectReason('');
    setRejectDialogOpen(true);
  };
  
  const confirmRejectCheck = async () => {
    if (!rejectingCheck) return;
    if (!rejectReason.trim()) {
      toast.error('Debe ingresar el motivo del rechazo');
      return;
    }
    try {
      await axios.put(
        `${BACKEND_URL}/api/treasury/checks/${rejectingCheck.check_id}/return`,
        { reason: rejectReason.trim() },
        { withCredentials: true }
      );
      toast.success('Cheque marcado como rechazado');
      setRejectDialogOpen(false);
      setRejectingCheck(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };
  
  const handleUseInPayment = (check) => {
    // Buscar el payment_type que sea cheque
    const checkPT = paymentTypes.find(pt => {
      const name = pt.name.toLowerCase();
      return name.includes('cheque') || name.includes('echeq') || name.includes('e.cheq');
    });
    
    setPaymentOrderForm({
      date: new Date().toISOString().split('T')[0],
      cash_bank_id: '',
      expense_concept_id: '',
      recipient_type: 'other',
      recipient_name: '',
      supplier_id: '',
      description: `Pago con cheque ${check.check_number} de ${check.issuer}`,
      payment_details: [{
        payment_type_id: checkPT?.payment_type_id || '',
        amount: check.amount || '',
        reference: `Cheque ${check.check_number}`,
        locked: true,
        check_id: check.check_id
      }],
      check_id: check.check_id
    });
    setPaymentOrderDialogOpen(true);
    toast.info(`Complete los datos para la Orden de Pago con cheque ${check.check_number}`);
  };
  
  const formatCurrency = (amount, currency = 'ARS') => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  };
  
  const formatDate = (dateStr) => (dateStr ? fmtDateTz(dateStr) : '-');
  
  const getCheckStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
      case 'deposited': return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Depositado</Badge>;
      case 'cashed': return <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="w-3 h-3 mr-1" />Cobrado</Badge>;
      case 'returned': return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      case 'paid': return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Pagado</Badge>;
      case 'delivered': return <Badge className="bg-indigo-100 text-indigo-800"><ArrowLeftRight className="w-3 h-3 mr-1" />Entregado</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const payIssuedCheck = async (check) => {
    if (!window.confirm(`¿Confirmás el PAGO del cheque diferido ${check.check_number || ''} por ${formatCurrency(check.amount)}? Se debitará la caja/banco origen.`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/treasury/checks/${check.check_id}/pay-issued`, {}, { withCredentials: true });
      toast.success('Cheque diferido pagado');
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al pagar el cheque'); }
  };

  const rejectIssuedCheck = async (check) => {
    const reason = window.prompt('Motivo del rechazo (opcional):', '');
    if (reason === null) return;
    try {
      await axios.put(`${BACKEND_URL}/api/treasury/checks/${check.check_id}/reject-issued`, { reason }, { withCredentials: true });
      toast.success('Cheque diferido rechazado');
      fetchAllData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al rechazar el cheque'); }
  };
  
  if (loading) {
    return <div className="flex items-center justify-center h-64"><p>Cargando...</p></div>;
  }
  
  const collectionTotal = collectionForm.payment_details.reduce((sum, pd) => sum + (parseFloat(pd.amount) || 0), 0);
  
  return (
    <div className="space-y-6" data-testid="treasury-operations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/treasury')} data-testid="back-to-treasury-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Operaciones de Tesorería</h1>
            <p className="text-muted-foreground">Cobranzas, Pagos, Ajustes y Valores</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full max-w-3xl">
          {canViewCobranzas && <TabsTrigger value="collections" className="text-xs flex-1" data-testid="tab-collections">Cobranzas</TabsTrigger>}
          {canViewPagos && <TabsTrigger value="payments" className="text-xs flex-1" data-testid="tab-payments">Pagos</TabsTrigger>}
          {canViewAjustes && <TabsTrigger value="adjustments" className="text-xs flex-1" data-testid="tab-adjustments">Ajustes</TabsTrigger>}
          {canViewTransferencias && <TabsTrigger value="transfers" className="text-xs flex-1" data-testid="tab-transfers">Transf.</TabsTrigger>}
          {canViewValores && <TabsTrigger value="checks" className="text-xs flex-1" data-testid="tab-checks">Valores</TabsTrigger>}
          {canViewLibro && <TabsTrigger value="ledger" className="text-xs flex-1" data-testid="tab-ledger">Libro</TabsTrigger>}
        </TabsList>
        
        {/* Filtros comunes */}
        <div className="flex flex-wrap items-end gap-3 mt-4 p-3 bg-muted/30 rounded-lg border" data-testid="operations-filters">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Desde</label>
              <Input type="date" className="h-8 w-36 text-sm" value={filters.dateFrom}
                onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))} data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Hasta</label>
              <Input type="date" className="h-8 w-36 text-sm" value={filters.dateTo}
                onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))} data-testid="filter-date-to" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Caja/Banco</label>
              <select className="h-8 w-44 px-2 rounded-md border border-input bg-background text-sm"
                value={filters.cashBankId} onChange={(e) => setFilters(f => ({ ...f, cashBankId: e.target.value }))} data-testid="filter-cash-bank">
                <option value="">Todas</option>
                {cashBanks.filter(cb => cb.is_active).map(cb => (
                  <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Buscar</label>
              <Input placeholder="Número, descripción, importe..." className="h-8 w-48 text-sm" value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="filter-search" />
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs" data-testid="clear-filters-btn">
              Limpiar
            </Button>
          </div>
        
        {/* TAB COBRANZAS */}
        {canViewCobranzas && <TabsContent value="collections" className="space-y-4 mt-6">
          <CollectionsTabContent
            canCreateCobranzas={canCreateCobranzas}
            onNewCollection={() => setCollectionDialogOpen(true)}
            filteredCollections={filteredCollections}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            downloadPdf={downloadPdf}
            canDeleteCollection={canDeleteCollection}
            onDeleteCollection={(c) => { setDeleteCollTarget(c); setDeleteCollReason(''); }}
          />
        </TabsContent>}
        
        {/* TAB PAGOS */}
        {canViewPagos && <TabsContent value="payments" className="space-y-4 mt-6">
          <PaymentsTabContent
            showDeletedOps={showDeletedOps}
            setShowDeletedOps={setShowDeletedOps}
            canCreatePagos={canCreatePagos}
            onNewPaymentOrder={() => setPaymentOrderDialogOpen(true)}
            filteredPaymentOrders={filteredPaymentOrders}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            openSettleDialog={openSettleDialog}
            downloadPdf={downloadPdf}
            canDeleteOp={canDeleteOp}
            onDeleteOp={(po) => { setDeleteOpTarget(po); setDeleteOpReason(""); }}
            canUnsettle={canUnsettleAdvance}
            onUnsettle={(po) => { setUnsettleTarget(po); setUnsettleReason(''); }}
          />
        </TabsContent>}
        
        {/* TAB AJUSTES */}
        {canViewAjustes && <TabsContent value="adjustments" className="space-y-4 mt-6">
          <Tabs defaultValue="aj-caja" className="w-full">
            <TabsList data-testid="adjustments-subtabs">
              <TabsTrigger value="aj-caja" data-testid="subtab-aj-caja">Ajustes de caja (AJ)</TabsTrigger>
              <TabsTrigger value="aj-cc" data-testid="subtab-aj-cc">Ajustes de Cta.Cte. (AJCC)</TabsTrigger>
            </TabsList>

            {/* SUB-TAB · AJUSTES DE CAJA (AJ) */}
            <TabsContent value="aj-caja" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-500" />
                  Ajustes de Tesorería (caja/banco)
                </h2>
                {canCreateAjustes && (
                  <Button onClick={() => setAdjustmentDialogOpen(true)} data-testid="new-adjustment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Ajuste
                  </Button>
                )}
              </div>

              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Fecha ajuste</TableHead>
                        <TableHead>Cargado el</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Caja/Banco</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead>Cta.Cte.</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead className="text-center">Ver</TableHead>
                        {canDeleteAdjustment && <TableHead className="text-center">Eliminar</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAdjustments.length === 0 ? (
                        <TableRow><TableCell colSpan={canDeleteAdjustment ? 10 : 9} className="text-center text-muted-foreground py-8">No hay ajustes que coincidan con los filtros</TableCell></TableRow>
                      ) : filteredAdjustments.map(adj => (
                        <TableRow key={adj.adjustment_id} className={adj.deleted_at ? 'opacity-60' : ''} data-testid={`adjustment-row-${adj.adjustment_number}`}>
                          <TableCell className="font-mono">
                            {adj.adjustment_number}
                            {adj.deleted_at && (
                              <span className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`adjustment-deleted-badge-${adj.adjustment_number}`}>ELIMINADO</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(adj.date)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDate(adj.input_at || adj.created_at)}</TableCell>
                          <TableCell>
                            {adj.adjustment_type === 'debit' ? (
                              <Badge className="bg-green-100 text-green-800">DÉBITO (+)</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">CRÉDITO (-)</Badge>
                            )}
                          </TableCell>
                          <TableCell>{adj.cash_bank_name}</TableCell>
                          <TableCell>
                            {adj.concept_name}
                            {adj.deleted_at && (
                              <span className="block text-xs text-muted-foreground">Eliminado por {adj.deleted_by_name} · {adj.deletion_reason}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {adj.target_type === 'supplier'
                              ? <span className="text-amber-800">Prov: {adj.target_snapshot?.business_name || '—'}</span>
                              : adj.target_type === 'resident'
                                ? <span className="text-blue-800">Res: {adj.target_snapshot?.full_name || '—'}</span>
                                : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${adj.deleted_at ? 'text-muted-foreground line-through' : (adj.adjustment_type === 'debit' ? 'text-green-600' : 'text-red-600')}`}>
                            {adj.adjustment_type === 'debit' ? '+' : '-'}{formatCurrency(adj.amount)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => setDocDetail({ kind: 'adjustments', id: adj.adjustment_id })}
                              title="Ver detalle" data-testid={`view-adjustment-${adj.adjustment_number}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                          {canDeleteAdjustment && (
                            <TableCell className="text-center">
                              {!adj.deleted_at && (
                                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700"
                                  onClick={() => { setDeleteAdjTarget(adj); setDeleteAdjReason(''); }}
                                  title="Eliminar ajuste (reversa todo)" data-testid={`delete-adjustment-${adj.adjustment_number}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB · AJUSTES DE CTA.CTE. (AJCC) */}
            <TabsContent value="aj-cc" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-500" />
                  Ajustes puros de Cuenta Corriente
                </h2>
              </div>
              <p className="text-xs text-muted-foreground">
                Ajustes contables que corrigen el saldo de la cuenta corriente del tercero sin mover caja/banco.
                Se crean desde la ficha de Cuenta Corriente del residente o proveedor. Acá se listan todos y pueden revertirse.
              </p>

              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Fecha ajuste</TableHead>
                        <TableHead>Cargado el</TableHead>
                        <TableHead>Efecto</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead>Cta.Cte.</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        {canDeleteAdjustment && <TableHead className="text-center">Revertir</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCCAdjustments.length === 0 ? (
                        <TableRow><TableCell colSpan={canDeleteAdjustment ? 8 : 7} className="text-center text-muted-foreground py-8">No hay ajustes de cuenta corriente que coincidan con los filtros</TableCell></TableRow>
                      ) : filteredCCAdjustments.map(adj => (
                        <TableRow key={adj.adjustment_id} className={adj.deleted_at ? 'opacity-60' : ''} data-testid={`cc-adjustment-row-${adj.adjustment_number}`}>
                          <TableCell className="font-mono">
                            {adj.adjustment_number}
                            {adj.deleted_at && (
                              <span className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`cc-adjustment-reverted-badge-${adj.adjustment_number}`}>REVERTIDO</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(adj.date)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDate(adj.input_at || adj.created_at)}</TableCell>
                          <TableCell>
                            {adj.direction === 'increase' ? (
                              <Badge className="bg-red-100 text-red-800">AUMENTA DEUDA (+)</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800">DISMINUYE DEUDA (-)</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {adj.concept_name}
                            {adj.deleted_at && (
                              <span className="block text-xs text-muted-foreground">Revertido por {adj.deleted_by_name} · {adj.deletion_reason}</span>
                            )}
                            {adj.description && <span className="block text-xs text-muted-foreground">{adj.description}</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {adj.target_type === 'supplier'
                              ? <span className="text-amber-800">Prov: {adj.target_snapshot?.business_name || '—'}</span>
                              : adj.target_type === 'resident'
                                ? <span className="text-blue-800">Res: {adj.target_snapshot?.full_name || '—'}</span>
                                : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${adj.deleted_at ? 'text-muted-foreground line-through' : (adj.direction === 'increase' ? 'text-red-600' : 'text-green-600')}`}>
                            {adj.direction === 'increase' ? '+' : '-'}{formatCurrency(adj.amount)}
                          </TableCell>
                          {canDeleteAdjustment && (
                            <TableCell className="text-center">
                              {!adj.deleted_at && (
                                <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700 border-rose-200"
                                  onClick={() => { setRevertCCTarget(adj); setRevertCCReason(''); }}
                                  title="Revertir ajuste (contra-asiento)" data-testid={`revert-cc-adjustment-${adj.adjustment_number}`}>
                                  Revertir
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>}
        
        {/* TAB TRANSFERENCIAS */}
        {canViewTransferencias && <TabsContent value="transfers" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-blue-500" />
              Transferencias Internas
            </h2>
            {canCreateTransferencias && (
              <Button onClick={() => setTransferDialogOpen(true)} data-testid="new-transfer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Transferencia
              </Button>
            )}
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-center">Ver</TableHead>
                    {canDeleteTransfer && <TableHead className="text-center">Eliminar</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.length === 0 ? (
                    <TableRow><TableCell colSpan={canDeleteTransfer ? 7 : 6} className="text-center text-muted-foreground py-8">No hay transferencias que coincidan con los filtros</TableCell></TableRow>
                  ) : filteredTransfers.map(tr => (
                    <TableRow key={tr.transfer_id} className={tr.deleted_at ? 'opacity-60' : ''} data-testid={`transfer-row-${tr.transfer_number}`}>
                      <TableCell className="font-mono">
                        {tr.transfer_number}
                        {tr.deleted_at && (
                          <span className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`transfer-deleted-badge-${tr.transfer_number}`}>ELIMINADA</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(tr.date)}</TableCell>
                      <TableCell>{tr.origin_cash_bank_name}</TableCell>
                      <TableCell>
                        {tr.destination_cash_bank_name}
                        {tr.deleted_at && (
                          <span className="block text-xs text-muted-foreground">Eliminada por {tr.deleted_by_name} · {tr.deletion_reason}</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${tr.deleted_at ? 'text-muted-foreground line-through' : ''}`}>
                        {formatCurrency(tr.amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="sm" onClick={() => setDocDetail({ kind: 'transfers', id: tr.transfer_id })}
                          title="Ver detalle" data-testid={`view-transfer-${tr.transfer_number}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      {canDeleteTransfer && (
                        <TableCell className="text-center">
                          {!tr.deleted_at && (
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700"
                              onClick={() => { setDeleteTrTarget(tr); setDeleteTrReason(''); }}
                              title="Eliminar transferencia (reversa todo)" data-testid={`delete-transfer-${tr.transfer_number}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>}
        
        {/* TAB VALORES EN CARTERA */}
        {canViewValores && <TabsContent value="checks" className="space-y-4 mt-6">
          <ChecksTabContent
            checks={checks}
            filteredChecks={filteredChecks}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getCheckStatusBadge={getCheckStatusBadge}
            canViewValores={canViewValores}
            payIssuedCheck={payIssuedCheck}
            rejectIssuedCheck={rejectIssuedCheck}
            onDeposit={(chk) => { setSelectedCheck(chk); setDepositDialogOpen(true); }}
            handleUseInPayment={handleUseInPayment}
            handleReturnCheck={handleReturnCheck}
          />
        </TabsContent>}
        
        {/* TAB LIBRO DIARIO */}
        {canViewLibro && <TabsContent value="ledger" className="space-y-4 mt-6">
          <LedgerTabContent
            filteredMovements={filteredMovements}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            onViewMovement={(m) => { setSelectedMovement(m); setViewMovementOpen(true); }}
          />
        </TabsContent>}
      </Tabs>

      <CollectionDialog
        open={collectionDialogOpen}
        onOpenChange={setCollectionDialogOpen}
        form={collectionForm}
        setForm={setCollectionForm}
        cashBanks={cashBanks}
        incomeConcepts={incomeConcepts}
        paymentTypes={paymentTypes}
        residents={residents}
        residentsAvailable={residentsAvailable}
        addPaymentDetail={addPaymentDetail}
        updatePaymentDetail={updatePaymentDetail}
        removePaymentDetail={removePaymentDetail}
        isCheckPaymentType={isCheckPaymentType}
        isTransferPaymentType={isTransferPaymentType}
        collectionTotal={collectionTotal}
        detectCashIntoBankConflict={detectCashIntoBankConflict}
        residentPending={residentPending}
        loadResidentPending={loadResidentPending}
        toggleResidentInvoice={toggleResidentInvoice}
        updateResidentInvoiceAmount={updateResidentInvoiceAmount}
        residentAllocTotal={residentAllocTotal}
        onSave={handleSaveCollection}
        onCancel={() => { setCollectionDialogOpen(false); resetCollectionForm(); }}
        submitting={collectionSubmit.submitting}
      />

      <PaymentOrderDialog
        open={paymentOrderDialogOpen}
        onOpenChange={(open) => { if (!open) resetPaymentOrderForm(); setPaymentOrderDialogOpen(open); }}
        form={paymentOrderForm}
        setForm={setPaymentOrderForm}
        cashBanks={cashBanks}
        suppliers={suppliers}
        expenseConcepts={expenseConcepts}
        paymentTypes={paymentTypes}
        supplierPending={supplierPending}
        setSupplierPending={setSupplierPending}
        loadSupplierPending={loadSupplierPending}
        supplierAdvanceTotal={supplierAdvanceTotal}
        supplierNcCreditTotal={supplierNcCreditTotal}
        applySupplierAdvanceAuto={applySupplierAdvanceAuto}
        togglePendingSelect={togglePendingSelect}
        updatePendingAmount={updatePendingAmount}
        debtAllocationsTotal={debtAllocationsTotal}
        addPOPaymentDetail={addPOPaymentDetail}
        updatePOPaymentDetail={updatePOPaymentDetail}
        removePOPaymentDetail={removePOPaymentDetail}
        isDeferredCheckPaymentType={isDeferredCheckPaymentType}
        isCashPaymentType={isCashPaymentType}
        detectCashIntoBankConflict={detectCashIntoBankConflict}
        onSave={handleSavePaymentOrder}
        onCancel={() => { setPaymentOrderDialogOpen(false); resetPaymentOrderForm(); }}
        submitting={paymentOrderSubmit.submitting}
      />

      
      

      <SettleAdvanceDialog
        open={settleDialogOpen}
        onOpenChange={(o) => { if (!o) { setSettleDialogOpen(false); setSettleAdvance(null); } }}
        onCancel={() => { setSettleDialogOpen(false); setSettleAdvance(null); }}
        advance={settleAdvance}
        form={settleForm}
        setForm={setSettleForm}
        setSettleExpense={setSettleExpense}
        rmSettleExpense={rmSettleExpense}
        addSettleExpense={addSettleExpense}
        addComprobante={addComprobante}
        setComprobante={setComprobante}
        rmComprobante={rmComprobante}
        expenseConcepts={expenseConcepts}
        suppliers={suppliers}
        withholdingTypes={withholdingTypes}
        settleRendido={settleRendido}
        settleDiff={settleDiff}
        cashBanks={cashBanks}
        onConfirm={handleSettle}
        settling={settling}
      />

      
      <AdjustmentDialog
        open={adjustmentDialogOpen}
        onOpenChange={setAdjustmentDialogOpen}
        form={adjustmentForm}
        setForm={setAdjustmentForm}
        onSave={handleSaveAdjustment}
        onCancel={() => { setAdjustmentDialogOpen(false); resetAdjustmentForm(); }}
        cashBanks={cashBanks}
        incomeConcepts={incomeConcepts}
        expenseConcepts={expenseConcepts}
        submitting={adjustmentSubmit.submitting}
      />

      <TransferDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        form={transferForm}
        setForm={setTransferForm}
        onSave={handleSaveTransfer}
        onCancel={() => { setTransferDialogOpen(false); resetTransferForm(); }}
        cashBanks={cashBanks}
        submitting={transferSubmit.submitting}
      />

      <CheckDialog
        open={checkDialogOpen}
        onOpenChange={setCheckDialogOpen}
        form={checkForm}
        setForm={setCheckForm}
        onSave={handleSaveCheck}
        onCancel={() => { setCheckDialogOpen(false); resetCheckForm(); }}
      />

      <DepositCheckDialog
        open={depositDialogOpen}
        onOpenChange={setDepositDialogOpen}
        check={selectedCheck}
        cashBanks={cashBanks}
        onDeposit={handleDepositCheck}
        onCancel={() => { setDepositDialogOpen(false); setSelectedCheck(null); }}
      />

      <RejectCheckDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        check={rejectingCheck}
        reason={rejectReason}
        setReason={setRejectReason}
        onConfirm={confirmRejectCheck}
        onCancel={() => setRejectDialogOpen(false)}
      />

      <TreasuryDocDetailDialog target={docDetail} onClose={() => setDocDetail(null)} />

      {/* DIALOG VER MOVIMIENTO */}
      <MovementDetailDialog
        open={viewMovementOpen}
        onOpenChange={setViewMovementOpen}
        movement={selectedMovement}
      />

      <DeletePaymentOrderDialog
        target={deleteOpTarget}
        reason={deleteOpReason}
        setReason={setDeleteOpReason}
        deleting={deletingOp}
        onConfirm={handleDeleteOp}
        onClose={() => { setDeleteOpTarget(null); setDeleteOpReason(''); }}
      />

      <DeleteCollectionDialog
        target={deleteCollTarget}
        reason={deleteCollReason}
        setReason={setDeleteCollReason}
        deleting={deletingColl}
        onConfirm={handleDeleteCollection}
        onClose={() => { setDeleteCollTarget(null); setDeleteCollReason(''); }}
      />

      <DeleteTreasuryDocDialog
        target={unsettleTarget}
        title="Anular rendición del anticipo"
        docLabel={`rendición del anticipo ${unsettleTarget?.payment_order_number || ''}`}
        amount={unsettleTarget?.rendicion?.rendido || unsettleTarget?.total_amount}
        detail="El anticipo vuelve a estado ABIERTO y podés rendirlo de nuevo o eliminar la OP. Se revierten: el acumulado de los conceptos de gasto rendidos, el movimiento y el saldo de la caja por la devolución/reposición de la diferencia, y los comprobantes fiscales que la rendición había cargado al Libro IVA Compras (cada uno con su contra-asiento). El asiento de la rendición se reversa con un contra-asiento; el asiento del anticipo no se toca."
        reason={unsettleReason}
        setReason={setUnsettleReason}
        deleting={unsettling}
        confirmLabel="Anular rendición"
        testId="unsettle"
        onConfirm={handleUnsettle}
        onClose={() => { setUnsettleTarget(null); setUnsettleReason(''); }}
      />

      <DeleteTreasuryDocDialog
        target={deleteAdjTarget}
        title="Eliminar Ajuste"
        docLabel={`ajuste ${deleteAdjTarget?.adjustment_number || ''}`}
        amount={deleteAdjTarget?.amount}
        detail="Se revertirán el saldo de la caja/banco y el acumulado del concepto, el movimiento queda tachado en el Libro de Tesorería y en contabilidad se genera un contra-asiento. El ajuste sigue en el listado marcado como eliminado."
        reason={deleteAdjReason}
        setReason={setDeleteAdjReason}
        deleting={deletingAdj}
        testId="adjustment"
        onConfirm={() => deleteWithReason(`/api/treasury/adjustments/${deleteAdjTarget.adjustment_id}`,
          deleteAdjReason, `Ajuste ${deleteAdjTarget.adjustment_number}`, setDeletingAdj,
          () => { setDeleteAdjTarget(null); setDeleteAdjReason(''); })}
        onClose={() => { setDeleteAdjTarget(null); setDeleteAdjReason(''); }}
      />

      <DeleteTreasuryDocDialog
        target={deleteTrTarget}
        title="Eliminar Transferencia"
        docLabel={`transferencia ${deleteTrTarget?.transfer_number || ''}`}
        amount={deleteTrTarget?.amount}
        detail={`El dinero vuelve de ${deleteTrTarget?.destination_cash_bank_name || 'destino'} a ${deleteTrTarget?.origin_cash_bank_name || 'origen'}: se revierten las dos cajas, los dos movimientos quedan tachados en el Libro de Tesorería y en contabilidad se genera un contra-asiento. La transferencia sigue en el listado marcada como eliminada.`}
        reason={deleteTrReason}
        setReason={setDeleteTrReason}
        deleting={deletingTr}
        testId="transfer"
        onConfirm={() => deleteWithReason(`/api/treasury/transfers/${deleteTrTarget.transfer_id}`,
          deleteTrReason, `Transferencia ${deleteTrTarget.transfer_number}`, setDeletingTr,
          () => { setDeleteTrTarget(null); setDeleteTrReason(''); })}
        onClose={() => { setDeleteTrTarget(null); setDeleteTrReason(''); }}
      />

      <DeleteTreasuryDocDialog
        target={revertCCTarget}
        title="Revertir Ajuste de Cuenta Corriente"
        docLabel={`ajuste ${revertCCTarget?.adjustment_number || ''}`}
        amount={revertCCTarget?.amount}
        detail="Se elimina el renglón del ajuste en la cuenta corriente del tercero y en contabilidad se genera un contra-asiento. No mueve caja/banco. El ajuste queda en el listado marcado como REVERTIDO."
        reason={revertCCReason}
        setReason={setRevertCCReason}
        deleting={revertingCC}
        testId="revert-cc-adjustment"
        confirmLabel="Revertir ajuste"
        onConfirm={() => revertWithReason(`/api/treasury/cc-adjustments/${revertCCTarget.adjustment_id}/reverse`,
          revertCCReason, `Ajuste ${revertCCTarget.adjustment_number}`, setRevertingCC,
          () => { setRevertCCTarget(null); setRevertCCReason(''); })}
        onClose={() => { setRevertCCTarget(null); setRevertCCReason(''); }}
      />

    </div>
  );
}

export default TreasuryOperations;
